import React, { useEffect } from 'react';
import {
  Animated,
  Platform,
  ScrollViewProps,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

import LessonButton from './lesson-button';
import useLessonButtonTopEdge from './lesson-button-top-edge';
import LessonPlayerFooter from './lesson-player-footer';
import { useActivityContext } from '../../hooks/use-activity.context';
import { useLessonSettings } from '../../hooks/use-lesson-settings';
import { useSafeState } from '../../hooks/use-safe-state';

interface LessonPlayerFrameProps extends ScrollViewProps {
  onCheckButtonPress?: () => void;
  showCheckButton?: boolean;
  showContinueButton: boolean;
  isButtonStuck?: boolean;
  // The prop scrollY is required when used StickAudioPlayer inside LessonPlayerFrame
  scrollY?: Animated.Value;
  children?: React.ReactNode;
}

const BUTTON_ANIMATION_DURATION = 350;

const LessonPlayerFrame: React.FC<LessonPlayerFrameProps> = ({
  children,
  onCheckButtonPress,
  showCheckButton = false,
  showContinueButton,
  isButtonStuck = true,
  scrollY,
  style,
  ...rest
}) => {
  const { goNextWithoutSteps } = useActivityContext();
  const { isFooterVisible } = useLessonSettings();
  const [animated, setAnimated] = useSafeState(true);

  const { setFooterHeight, setViewHeight, setContainerHeight, topEdge } =
    useLessonButtonTopEdge();

  const handleLessonButtonPress = () => {
    if (showContinueButton) {
      goNextWithoutSteps();
    } else {
      onCheckButtonPress?.();
    }
  };

  useEffect(() => {
    if (showCheckButton || showContinueButton) {
      setTimeout(() => setAnimated(false), BUTTON_ANIMATION_DURATION);
    } else {
      setAnimated(true);
    }
  }, [showCheckButton, showContinueButton, setAnimated]);

  const showWebButton =
    Platform.OS === 'web' && (showCheckButton || showContinueButton);
  const showNativeButton =
    Platform.OS !== 'web' && (showCheckButton || showContinueButton);

  const lessonButton = (
    <LessonButton
      key={showContinueButton ? 'button.continue' : 'button.checkAnswer'}
      i18nKey={showContinueButton ? 'button.continue' : 'button.checkAnswer'}
      animationDuration={BUTTON_ANIMATION_DURATION}
      animated={animated}
      onPress={handleLessonButtonPress}
    />
  );

  return (
    <View
      style={styles.container}
      onLayout={(e) => setViewHeight(e.nativeEvent.layout.height)}
    >
      <Animated.ScrollView
        nestedScrollEnabled
        bounces={false}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        scrollEventThrottle={4}
        onScroll={
          scrollY &&
          Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
            useNativeDriver: true,
          })
        }
        style={[styles.container, style]}
        contentContainerStyle={styles.scrollView}
        {...rest}
      >
        <View
          onLayout={(e) => setContainerHeight(e.nativeEvent.layout.height)}
          style={styles.innerContainer}
        >
          <View style={styles.content}>{children}</View>

          {showNativeButton && <View style={styles.stuckButtonPadding} />}

          {showWebButton && (
            <View style={isButtonStuck ? stickyStyle : null}>
              {lessonButton}
            </View>
          )}

          {isFooterVisible && (
            <LessonPlayerFooter
              onLayout={(e) => setFooterHeight(e.nativeEvent.layout.height)}
            />
          )}
        </View>
      </Animated.ScrollView>

      {showNativeButton && (
        <Animated.View
          style={[
            styles.stuckButtonContainer,
            scrollY && {
              transform: [
                {
                  translateY: scrollY.interpolate({
                    inputRange: [topEdge - 1, topEdge, topEdge + 1],
                    outputRange: [isButtonStuck ? 0 : 1, 0, -1],
                  }),
                },
              ],
            },
          ]}
        >
          {lessonButton}
        </Animated.View>
      )}
    </View>
  );
};

export default LessonPlayerFrame;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flexGrow: 1,
  },
  innerContainer: {
    flex: 1,
    minHeight: '100%',
    justifyContent: 'space-between',
  },
  content: {
    flex: 1,
    paddingBottom: 40,
  },
  stuckButtonContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  stuckButtonPadding: {
    height: 56,
  },
});

// Position sticky is supported in web but not on mobile, so the typings aren't there
// and we have to use @ts-ignore to remove the lint error
// @ts-ignore
const stickyStyle: StyleProp<ViewStyle> = {
  position: 'sticky',
  bottom: 0,
};
